import {
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import {
  Box,
  Card,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useProductCacheList,
  useProductCacheMutations,
} from "../../../hooks/useProductCacheAdmin";
import ProductCacheDetailModal from "./ProductCacheDetailModal";

const formatTimeAgo = (date: string) => {
  const diff = Date.now() - new Date(date).getTime();
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days} day${days > 1 ? "s" : ""} ago`;
  if (hours > 0) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  if (minutes > 0) return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
  return `${seconds} second${seconds > 1 ? "s" : ""} ago`;
};

const ProductCacheTable: React.FC = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [retailer, setRetailer] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<{
    productId: string;
    retailer: string;
  } | null>(null);

  const { data, isLoading, refetch } = useProductCacheList(
    page + 1,
    pageSize,
    retailer === "all" ? undefined : retailer,
    searchTerm || undefined
  );
  const { deleteProduct } = useProductCacheMutations();

  const products = data?.data?.items || [];
  const totalCount = data?.data?.totalCount || 0;

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleView = (productId: string, retailer: string) => {
    setSelectedProduct({ productId, retailer });
    setViewModalOpen(true);
  };

  const handleDelete = async (productId: string, retailer: string) => {
    if (window.confirm(t("admin.productCache.confirmDelete", { productId, retailer }))) {
      await deleteProduct.mutateAsync({ productId, retailer });
    }
  };

  return (
    <Box>
      <Card sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <Select
            value={retailer}
            onChange={(e) => {
              setRetailer(e.target.value);
              setPage(0);
            }}
            size="small"
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="all">{t("admin.productCache.allRetailers")}</MenuItem>
            <MenuItem value="amazon">Amazon</MenuItem>
            <MenuItem value="walmart">Walmart</MenuItem>
          </Select>

          <TextField
            placeholder={t("admin.productCache.searchByProductId")}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            size="small"
            sx={{ minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
              ),
            }}
          />

          <Box sx={{ flexGrow: 1 }} />

          <Tooltip title={t("admin.productCache.refresh")}>
            <IconButton onClick={() => refetch()} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Card>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t("admin.productCache.productId")}</TableCell>
              <TableCell>{t("admin.productCache.retailer")}</TableCell>
              <TableCell>{t("admin.productCache.cached")}</TableCell>
              <TableCell>{t("admin.productCache.updated")}</TableCell>
              <TableCell>{t("admin.productCache.expires")}</TableCell>
              <TableCell>{t("admin.productCache.status")}</TableCell>
              <TableCell align="right">{t("admin.productCache.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  {t("common.loading")}
                </TableCell>
              </TableRow>
            ) : products.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  {t("admin.productCache.noCachedProducts")}
                </TableCell>
              </TableRow>
            ) : (
              products.map((product: any) => (
                <TableRow key={`${product.productId}-${product.retailer}`}>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: "monospace" }}
                    >
                      {product.productId}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={product.retailer}
                      size="small"
                      color={
                        product.retailer === "amazon" ? "primary" : "secondary"
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip
                      title={new Date(product.cachedAt).toLocaleString()}
                    >
                      <Typography variant="body2">
                        {formatTimeAgo(product.cachedAt)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Tooltip
                      title={new Date(product.updatedAt).toLocaleString()}
                    >
                      <Typography variant="body2">
                        {formatTimeAgo(product.updatedAt)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {product.expiresAt ? (
                      <Tooltip
                        title={new Date(product.expiresAt).toLocaleString()}
                      >
                        <Typography variant="body2">
                          {formatTimeAgo(product.expiresAt)}
                        </Typography>
                      </Tooltip>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={product.isExpired ? t("admin.productCache.expired") : t("common.active")}
                      size="small"
                      color={product.isExpired ? "error" : "success"}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t("admin.productCache.viewJson")}>
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleView(product.productId, product.retailer)
                        }
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("common.delete")}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() =>
                          handleDelete(product.productId, product.retailer)
                        }
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={pageSize}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </TableContainer>

      {selectedProduct && (
        <ProductCacheDetailModal
          open={viewModalOpen}
          onClose={() => {
            setViewModalOpen(false);
            setSelectedProduct(null);
          }}
          productId={selectedProduct.productId}
          retailer={selectedProduct.retailer}
        />
      )}
    </Box>
  );
};

export default ProductCacheTable;
